import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import SupportChatWidget from '@/components/SupportChatWidget';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';

/**
 * AppChrome
 * - Sets <html lang> based on the active language (a11y)
 * - Adds a skip-to-content link (WCAG 2.4.1)
 * - Wraps children in <main id="main-content"> for semantics + skip target
 * - Renders global site Footer on all public routes
 *   (hidden inside the /admin & /portal & /auditor dashboards which have their own chrome)
 * - Loads global SEO settings once and applies them as BASE document meta.
 *   Per-page useDocumentMeta still overrides on mount and restores these on unmount.
 */

function applyBaseSeo(bundle) {
  if (!bundle || typeof document === 'undefined') return;
  const seo = bundle.globals || bundle;  // tolerant to flat or wrapped shape
  const page = bundle.page || {};

  // ──────────────────────────────────────────────────────────────────────────
  // Title / description / canonical / og:* / twitter:* are owned by:
  //   1. public/index.html bootstrap → /api/seo/meta?path=X   (first paint)
  //   2. page-level <SeoHead> via react-helmet-async         (post-hydration)
  //
  // AppChrome intentionally NO LONGER sets those — otherwise it fought with
  // the bootstrap on every route change and clobbered per-page SEO with the
  // CMS default "Zertivo — Vertrauen ist der neue Asset". Only truly-global
  // branding tags (site_name, locale, twitter handle) live here now.
  // ──────────────────────────────────────────────────────────────────────────
  const upsert = (selector, create, content) => {
    if (!content) return;
    let el = document.head.querySelector(selector);
    if (!el) { el = create(); document.head.appendChild(el); }
    if (el.getAttribute('content') !== content) el.setAttribute('content', content);
  };

  upsert('meta[property="og:site_name"]', () => { const m = document.createElement('meta'); m.setAttribute('property', 'og:site_name'); return m; }, seo.site_name);
  upsert('meta[property="og:locale"]',    () => { const m = document.createElement('meta'); m.setAttribute('property', 'og:locale'); return m; }, seo.locale);
  if (seo.twitter_handle) upsert('meta[name="twitter:site"]', () => { const m = document.createElement('meta'); m.setAttribute('name', 'twitter:site'); return m; }, seo.twitter_handle);

  // Hard de-dup: multiple head-meta sources (bootstrap + Helmet + legacy
  // public/index.html tags) can co-exist briefly. Keep only the last
  // occurrence so Google always sees exactly one title / canonical / etc.

  // (Legacy per-page upserts for title/description/og/twitter removed.
  // See comment above applyBaseSeo — bootstrap + Helmet are the single
  // source of truth now. Only the site-wide tags above remain.)

  const dedupSelectors = [
    'link[rel="canonical"]',
    'meta[name="robots"]',
    'meta[name="description"]',
    'meta[property="og:url"]',
    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[property="og:image"]',
    'meta[property="og:type"]',
    'meta[name="twitter:card"]',
    'meta[name="twitter:title"]',
    'meta[name="twitter:description"]',
    'meta[name="twitter:image"]',
  ];
  const dedupHead = () => {
    for (const sel of dedupSelectors) {
      const nodes = document.head.querySelectorAll(sel);
      if (nodes.length > 1) {
        for (let i = 0; i < nodes.length - 1; i++) nodes[i].remove();
      }
    }
  };
  dedupHead();
  setTimeout(dedupHead, 0);
  setTimeout(dedupHead, 200);

  // JSON-LD block — only replace the CMS-managed one (data-zv-seo).
  // Helmet-emitted ld+json blocks have data-rh, so they are never touched.
  let ldEl = document.head.querySelector('script[type="application/ld+json"][data-zv-seo="1"]');
  if (page.json_ld && typeof page.json_ld === 'object') {
    if (!ldEl) {
      ldEl = document.createElement('script');
      ldEl.setAttribute('type', 'application/ld+json');
      ldEl.setAttribute('data-zv-seo', '1');
      document.head.appendChild(ldEl);
    }
    ldEl.textContent = JSON.stringify(page.json_ld);
  } else if (ldEl) {
    ldEl.remove();
  }
}

export default function AppChrome({ children }) {
  const { lang } = useLanguage();
  const location = useLocation();
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang || 'de';
    }
  }, [lang]);

  // Load SEO (globals + per-page) on every route change.
  useEffect(() => {
    let alive = true;
    // Normalise path → match catalog entries (e.g., /profile/acme → /profile/:slug)
    const raw = location.pathname;
    let normalised = raw;
    if (raw.startsWith('/profile/') && raw !== '/profile') normalised = '/profile/:slug';
    else if (raw.startsWith('/blog/') && raw !== '/blog') normalised = '/blog/:slug';
    api.get(`/seo/public?path=${encodeURIComponent(normalised)}`)
      .then((r) => { if (alive) { setSeo(r.data); applyBaseSeo(r.data); } })
      .catch(() => {});

    // Also re-run the per-route meta bootstrap for SPA navigations
    // (the <script> in index.html only fires on initial page load).
    // This keeps <title>/<meta> correctly updated for Googlebot and
    // social-crawlers that follow internal SPA links.
    (async () => {
      try {
        const r = await fetch(`/api/seo/meta?path=${encodeURIComponent(raw)}`, { cache: 'default', credentials: 'omit' });
        if (!r.ok || !alive) return;
        const m = await r.json();
        if (!alive || !m || !m.title) return;
        const upMeta = (attr, key, content) => {
          if (!content) return;
          let el = document.head.querySelector(`meta[${attr}="${key}"]`);
          if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
          if (el.getAttribute('content') !== content) el.setAttribute('content', content);
        };
        const upLink = (rel, href) => {
          if (!href) return;
          let el = document.head.querySelector(`link[rel="${rel}"]`);
          if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
          if (el.getAttribute('href') !== href) el.setAttribute('href', href);
        };
        // Only set title if no page-level Helmet has already customised it
        // (Helmet stamps its tags with data-rh attr where supported).
        const titleEl = document.querySelector('title');
        const helmetOwnsTitle = titleEl && titleEl.hasAttribute('data-rh');
        if (!helmetOwnsTitle) document.title = m.title;
        upMeta('name', 'description', m.description);
        upMeta('name', 'robots', m.robots);
        upLink('canonical', m.canonical);
        upMeta('property', 'og:title', m.og_title);
        upMeta('property', 'og:description', m.og_description);
        upMeta('property', 'og:url', m.canonical);
        upMeta('property', 'og:image', m.og_image);
        upMeta('property', 'og:type', m.og_type);
        upMeta('name', 'twitter:title', m.og_title);
        upMeta('name', 'twitter:description', m.og_description);
        upMeta('name', 'twitter:image', m.og_image);
      } catch { /* non-critical */ }
    })();

    const onUpdate = () => {
      // refetch w/ current path when admin saves
      api.get(`/seo/public?path=${encodeURIComponent(normalised)}`)
        .then((r) => { if (alive) { setSeo(r.data); applyBaseSeo(r.data); } })
        .catch(() => {});
    };
    window.addEventListener('zertivo:seo-updated', onUpdate);
    return () => { alive = false; window.removeEventListener('zertivo:seo-updated', onUpdate); };
  }, [location.pathname]);

  // Canonical link is handled inside applyBaseSeo — no separate effect needed.
  useEffect(() => { /* noop — kept for potential future per-route side effects */ }, [location.pathname, seo]);

  const dashboardPaths = ['/admin', '/portal', '/auditor', '/onboarding'];
  const authPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
  // PoC pages have their own custom chrome (back-button + minimal footer) so
  // we hide the marketing Navbar/Footer here for a clean, distraction-free
  // reading experience.
  const isPoc = location.pathname === '/poc' || location.pathname.startsWith('/poc/');
  const isDashboard = dashboardPaths.some((p) => location.pathname.startsWith(p));
  const isAuth = authPaths.some((p) => location.pathname.startsWith(p));
  // Hide footer + navbar on authenticated dashboards + auth screens (custom chrome)
  const hideFooter = isDashboard || isAuth || isPoc;
  const hideNavbar = isDashboard || isAuth || isPoc;
  // Cinematic hero routes have a dark full-bleed hero that should overlap the
  // (now fixed) navbar. All other public routes need a top spacer the size of
  // the navbar (h-16/68px) so content doesn't slip beneath it.
  const isHeroRoute = location.pathname === '/' || location.pathname === '/about';
  const needsTopSpacer = !hideNavbar && !isHeroRoute;

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:px-4 focus:py-2 focus:bg-[#00D387] focus:text-black focus:font-medium focus:rounded-lg focus:shadow-xl"
        data-testid="skip-to-content"
      >
        {lang === 'en' ? 'Skip to content' : 'Zum Inhalt springen'}
      </a>
      {!hideNavbar && <Navbar />}
      <main id="main-content" role="main" tabIndex={-1} className={`focus:outline-none ${needsTopSpacer ? 'pt-16 md:pt-[68px]' : ''}`}>
        {children}
      </main>
      {!hideFooter && <Footer />}
      {/* Live-Chat — the floating bubble is the primary support channel since
          we don't offer phone support. Visible on every public page. Hidden
          only on the admin-side support view to avoid UI conflict. */}
      {!location.pathname.startsWith('/admin/support') && (
        <SupportChatWidget />
      )}
    </>
  );
}

